import { Injectable, inject } from '@angular/core';
import { IResponseMeta } from '@df/api/request/request.service';
import { IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Client } from '@df/core/service/client';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { GTMServiceEventAfter, GtmService } from '@df/module-gtm/gtm.service';
import { NOTICES } from '@df/ui/api/notice/notice.const';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import each from 'lodash-es/each';
import find from 'lodash-es/find';
import isString from 'lodash-es/isString';
import isUndefined from 'lodash-es/isUndefined';
import { Product } from '../../catalog/product/product.decorator';
import { ProductStockDescriptionService } from '../../catalog/product/stock-levels/product-stock-description.service';
export const GTM_FORM_FIELD_ERROR_MESSAGE = 'Required fields are missing or invalid';
declare module '@df/module-gtm/gtm.namespace' {
    namespace Gtm {
        export interface IProductImpressionData {
            item_brand?: string;
            item_category?: string;
            item_category2?: string;
            item_category3?: string;
            item_category4?: string;
            item_category5?: string;
            item_id?: string;
            item_name?: string;
            item_variant?: string;
        }

        export interface IEcommerceItem {
            id?: number;
        }

        export interface INavigationData {
            event: string;
            nav_category: string;
            nav_type?: string;
            nav_subcategory?: string;
            link_url?: string;
            link_text?: string;
        }

        export interface IAddToBasketBEEvent extends IEvent {
            ecommerce?: {
                add?: {
                    products?: IProductImpressionData[];
                };
            };
        }
    }
}
@Injectable({
    providedIn: 'root'
})
export class LocalGtmService extends GtmService {
    searchDesign?: string

    protected client = inject(Client);

    private productStockDescriptionService = inject(ProductStockDescriptionService);

    /**
     * if external cookie consent script is loaded and events can be pushed to GTM
     */
    private cmpLoaded: boolean = false;
    /**
     * number of checks if cmp is loaded
     */
    private cmpChecks = 0;

    /**
     * scheduled events that will be pushed to GTM once cmp is loaded
     */
    private scheduledEvents?: Gtm.IEvent[];

    override getImpressionDataFromProduct(product: Product<IProductDataV2>): Gtm.IProductImpressionData {
        const superData = <Gtm.IProductImpressionData>super.getImpressionDataFromProduct(product);
        if (product?.data) {
            superData.item_id = product.data.sku;
            superData.item_name = product.data.name;
            this.getItemData(superData, product);
        }
        return superData;
    }

    override getAdditionalProductData(product: Product<IProductDataV2>) {
        const superData = super.getAdditionalProductData(product);
        if (product?.data) {
            this.getItemData(superData, product);
        }
        return superData;
    }

    private getItemData(superData: any, product: Product<IProductDataV2>) {
        superData.id = product.data?.id;
        superData.item_variant = <string>optionLabelPipe(product.data?.variant_attribute_1, 'variant_attribute_1');
        superData.item_brand = <string>optionLabelPipe(product.data?.nyw_item_brand_desc_nh_bs, 'nyw_item_brand_desc_nh_bs');
        superData.category = product.data?.category_path || this.getDefaultListName('/', product.data?.breadcrumbs || []);
        superData.item_category = <string>optionLabelPipe((product.data as IProductDataV3).storedepartmentcode, 'storedepartmentcode');
        superData.item_category2 = <string>optionLabelPipe(product.data?.nyw_item_category_desc_nh_bs, 'nyw_item_category_desc_nh_bs');
        superData.item_category3 = this.getImpressionDataFromProductCategory3(product as Product<IProductDataV3>);
        superData.item_category4 = this.getImpressionDataFromProductCategory4(product);
        superData.item_category5 = <string>optionLabelPipe(product.data?.nyw_item_range_desc_nh_bs, 'nyw_item_range_desc_nh_bs');
    }

    private getImpressionDataFromProductCategory3(product: Product<IProductDataV3>): string | undefined {
        if (product.isClearance) {
            return 'Store clearance';
        }
        return product.data?.category_ids?.includes(505) ? 'Online Clearance' : 'Regular';
    }

    private getImpressionDataFromProductCategory4(product: Product<IProductDataV2>): string {
        return product.isOutOfStock ? 'Out of stock' : 'In stock';
    }

    protected override setListeners() {
        super.setListeners();
        this.setCheckoutSuccessTracking();
        this.cmpChecks = 0;
        this.setCmpTracking();
    }

    setCheckoutSuccessTracking() {
        this.app.events.on(Checkout.EVENTS.SuccessTracking).subscribe(data => {
            if (data.isSamples) {
                this.sendEventGA4('order_sample', {
                    items: data.items.map(item => {
                        return {
                            sample_colour: item.name
                        };
                    })
                });
            }
        });
    }

    private setCmpTracking() {
        if (typeof window === 'undefined') {
            return;
        }
        if (this.cmpChecks < 5) {
            this.timeoutService.setTimeout(() => {
                // if cmp is loaded, push all scheduled events
                if (window.dataLayer?.find(e => e.event === '__cmpLoaded')) {
                    this.sendScheduledEvents();
                } else {
                    this.cmpChecks++;
                    this.setCmpTracking();
                }
            }, 1000);
        } else {
            this.sendScheduledEvents();
        }
    }
    private sendScheduledEvents() {
        this.cmpLoaded = true;
        this.scheduledEvents?.forEach(event => {
            this.push(event);
        });
        this.scheduledEvents = [];
    }

    handleNavigationClick(data: Gtm.INavigationData) {
        let eventData = {
            ...data,
            nav_type: this.client.isX || this.client.isL ? 'Desktop' : 'Mobile'
        };
        this.push(eventData);
    }

    sendFormError(error: string, form: string, form_step?: number, coupon?: string): void {
        this.sendEventGA4('error_tracking', { error_type: 'form_field', error_message: NOTICES[error] || error, form, form_step, coupon });
    }
    override sendEventGA4(eventName: string, eventDetails: Gtm.IEventDataGA4 = {}, isNonInteraction: boolean = false): void {
        const event = <Gtm.IEventGA4>{
            event: eventName,
            ga4_event_data: eventDetails
        };
        this.push(event);
    }

    override push(eventData: Gtm.IEvent): void {
        if (eventData.event === 'view_item_ga4' && (<Gtm.IEventViewItem>eventData)?.ecommerce?.items) {
            (<Gtm.IEventViewItem>eventData).ecommerce.items.forEach(product => {
                product.item_category4 = this.productStockDescriptionService.get(1, <number>product.id, true);
            });
        }
        if (this.cmpLoaded) {
            if (typeof window === 'undefined') {
                return;
            }

            if (window.dataLayer === undefined) {
                window.dataLayer = [];
            }

            // strip off ecommerce data if disabled
            if (eventData['ecommerce']) {
                window.dataLayer.push({ ecommerce: undefined } as any); // without this ecommerce impressions are merged with previous data
            }

            /**
             * Ensure the data layer event elements will not merge with previous pushes.
             * In most cases we want to prevent the 'ecommerce' attribute to merge with old values.
             */

            if (window.google_tag_manager) {
                const gtmDl = find(window.google_tag_manager, (value, key) => isString(key) && (key as string).indexOf('GTM') == 0);
                if (gtmDl) {
                    Object.keys(eventData).forEach(key => {
                        gtmDl.dataLayer.set(key, undefined);
                    });
                    //Reset general event properties
                    each(['event', 'eventCategory', 'eventAction', 'eventLabel', 'eventValue', 'ecommerce'], propertyName => {
                        if (isUndefined(eventData[propertyName])) {
                            gtmDl.dataLayer.set(propertyName, undefined);
                        }
                    });
                }
            }

            if (['addToCart', 'checkoutSuccess'].indexOf(eventData['event']) !== -1) {
                eventData['eventLabel'] === '' && delete eventData['eventLabel'];
                eventData['eventValue'] === '' && delete eventData['eventValue'];
            }

            window.dataLayer.push(eventData);

            this.events.next(new GTMServiceEventAfter(eventData));
        } else {
            !this.scheduledEvents && (this.scheduledEvents = []);
            this.scheduledEvents?.push(eventData);
        }
    }

    /**
     * Track event coming from BE response
     *
     * @param eventData {Gtm.IEvent}
     *
     * @info Ready for GA4
     */
    protected override trackBackendEvent(eventData: Gtm.IEvent, response?: IResponseMeta) {
        this.timeoutService.setTimeout(() => {
            if (eventData?.event === 'addToCart' && (<Gtm.IAddToBasketBEEvent>eventData)?.ecommerce?.add?.products) {
                (<Gtm.IAddToBasketBEEvent>eventData)?.ecommerce?.add?.products?.forEach(product => {
                    product.item_category4 = this.productStockDescriptionService.get(0, product.product_id, true);
                });
            }
            if (eventData?.event === 'checkoutSuccess' && (<Gtm.IEventPurchase>eventData)?.ecommerce?.purchase?.products) {
                const leadTimes = this.productStockDescriptionService.getBasketLeadTimes();
                if (leadTimes?.length) {
                    (<Gtm.IEventPurchase>eventData).ecommerce.purchase.products.forEach((product, index) => {
                        product.item_category4 = leadTimes[index];
                    });
                    this.productStockDescriptionService.deleteBasketLeadTimes();
                }
            }
            super.trackBackendEvent(eventData, response);
        }, 20);
    }


    override trackSearchResult(search: { term: string; autocomplete: boolean; redirect?: string; numberOfResults: number }) {
        if (search.term) {
            const eventData: Gtm.IEventSearch = {
                event: 'search',
                search_term: search.term?.toLowerCase(),
                search_type: search.autocomplete ? 'preview' : search.redirect ? 'redirect' : 'standard',
                number_of_results: search.numberOfResults
            };
            if(this.searchDesign){
                eventData['search_design'] = this.searchDesign
            }
            this.push(eventData);
        }
    }
}
